<template>
  <div>
    <BaseToolBar
      title="Sinistres"
      subtitle="Liste des sinistres déclarés pour ce véhicule"
    >
      <BaseButton
        class="btn btn-primary ml-3"
        v-tooltip="'Ajouter un sinistre'"
        @click="addSinistre"
        :loading="loading"
        icon="plus"
        text="Ajouter"
      ></BaseButton>
    </BaseToolBar>

    <!-- Feedback message -->
    <div v-if="feedback.message" :class="`alert alert-${feedback.type} mt-3`">
      {{ feedback.message }}
    </div>

    <!-- Liste des sinistres -->
    <div
      v-for="(sinistre, index) in vehicule.sinistres"
      :key="sinistre.id || 'new_' + index"
      class="card mb-3"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center"
        :class="{ 'bg-warning': isEnAttente(sinistre) }"
      >
        <h6 class="mb-0">
          Sinistre {{ index + 1 }}
          <span
            v-if="sinistre.numero_sinistre"
            class="badge badge-warning ml-2"
          >
            N° {{ sinistre.numero_sinistre }}
          </span>
          <span
            v-if="sinistre.date_sinistre"
            class="badge badge-secondary ml-2"
          >
            {{ formatDate(sinistre.date_sinistre) }}
          </span>
          <span
            v-if="sinistre.etat !== null && sinistre.etat !== undefined"
            :class="getEtatBadgeClass(sinistre.etat)"
            class="badge ml-2"
          >
            {{ getEtatLabel(sinistre.etat) }}
          </span>
        </h6>
        <div>
          <ButtonCircle
            v-if="
              !sinistreEnCoursEdition ||
                sinistreEnCoursEdition.id !== sinistre.id
            "
            name="edit"
            @click="editSinistre(sinistre)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Modifier'"
          ></ButtonCircle>
          <ButtonCircle
            name="trash"
            @click="deleteSinistre(sinistre)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Supprimer'"
          ></ButtonCircle>
        </div>
      </div>
      <div class="card-body">
        <!-- Vue édition -->
        <div
          v-if="
            sinistreEnCoursEdition && sinistreEnCoursEdition.id === sinistre.id
          "
        >
          <base-form-row row>
            <base-inputDatePicker
              v-model="sinistreEnCoursEdition.date_sinistre"
              inputText="Date du sinistre"
            ></base-inputDatePicker>
            <base-input
              v-model="sinistreEnCoursEdition.numero_sinistre"
              inputText="Numéro de sinistre"
            ></base-input>
          </base-form-row>

          <base-form-row row>
            <select-collaborateur
              class="mb-1"
              v-model.number="sinistreEnCoursEdition.collaborateur_id"
              text="Collaborateur concerné"
            ></select-collaborateur>
            <base-inputDatePicker
              v-model="sinistreEnCoursEdition.date_expertise"
              inputText="Date d'expertise"
            ></base-inputDatePicker>
          </base-form-row>

          <base-form-row row>
            <base-select
              v-model.number="sinistreEnCoursEdition.type_dommage"
              inputText="Type de dommage"
              :options="typesDommage"
            ></base-select>
            <base-select
              v-model.number="sinistreEnCoursEdition.circonstance_dommage"
              inputText="Circonstance"
              :options="circonstancesDommage"
            ></base-select>
          </base-form-row>

          <base-form-row row>
            <base-input
              v-model="sinistreEnCoursEdition.desc_type"
              inputText="Détail du type de dommage"
            ></base-input>
            <base-input
              v-model="sinistreEnCoursEdition.desc_circonstance"
              inputText="Détail de la circonstance"
            ></base-input>
          </base-form-row>

          <base-form-row row>
            <base-select
              v-model.number="sinistreEnCoursEdition.responsabilite"
              inputText="Responsabilité"
              :options="optionsResponsabilite"
            ></base-select>
            <base-select
              v-model.number="sinistreEnCoursEdition.franchise"
              inputText="Franchise"
              :options="optionsFranchise"
            ></base-select>
          </base-form-row>

          <base-form-row row>
            <base-input
              v-model="sinistreEnCoursEdition.cout_societe"
              inputText="Coût pour la société (€)"
              type="number"
              step="0.01"
            ></base-input>
            <base-input
              v-model="sinistreEnCoursEdition.cout_assurance"
              inputText="Coût pour l'assurance (€)"
              type="number"
              step="0.01"
            ></base-input>
          </base-form-row>

          <base-form-row row>
            <base-input
              v-model="sinistreEnCoursEdition.garage_nom"
              inputText="Nom du garage"
            ></base-input>
            <base-input
              v-model="sinistreEnCoursEdition.garage_adresse"
              inputText="Adresse du garage"
            ></base-input>
          </base-form-row>

          <base-form-row>
            <base-select
              v-model.number="sinistreEnCoursEdition.etat"
              inputText="État du sinistre"
              :options="etatsOptions"
            ></base-select>
          </base-form-row>

          <base-form-row>
            <base-input
              v-model="sinistreEnCoursEdition.commentaires"
              inputText="Commentaires"
              type="textarea"
              rows="3"
            ></base-input>
          </base-form-row>

          <div class="d-flex justify-content-end mt-3">
            <button
              class="btn btn-secondary mr-2"
              @click="cancelEdit()"
              :disabled="loading"
            >
              Annuler
            </button>
            <button
              class="btn btn-primary"
              @click="saveSinistreEnCours()"
              :disabled="loading"
            >
              <i class="fas fa-save mr-1"></i>
              Enregistrer
            </button>
          </div>
        </div>

        <!-- Vue normale (non-édition) -->
        <div v-else>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Date du sinistre:</b>
              {{
                sinistre.date_sinistre
                  ? formatDate(sinistre.date_sinistre)
                  : "Non définie"
              }}
            </div>
            <div class="col-md-6">
              <b>Numéro:</b> {{ sinistre.numero_sinistre || "Non défini" }}
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <b>Collaborateur concerné:</b>
              {{ getCollaborateurName(sinistre.collaborateur_id) }}
            </div>
            <div class="col-md-6">
              <b>Date d'expertise:</b>
              {{
                sinistre.date_expertise
                  ? formatDate(sinistre.date_expertise)
                  : "Non définie"
              }}
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <b>Type de dommage:</b>
              {{ getTypeDommageLabel(sinistre.type_dommage) }}
            </div>
            <div class="col-md-6">
              <b>Circonstance:</b>
              {{ getCirconstanceLabel(sinistre.circonstance_dommage) }}
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <b>Détail du type de dommage:</b>
              {{ sinistre.desc_type || "" }}
            </div>
            <div class="col-md-6">
              <b>Détail de la circonstance:</b>
              {{ sinistre.desc_circonstance || "" }}
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <b>Responsabilité:</b>
              {{ getResponsabiliteLabel(sinistre.responsabilite) }}
            </div>
            <div class="col-md-6">
              <b>Franchise:</b>
              {{ getFranchiseLabel(sinistre.franchise) }}
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <b>Coût société:</b> {{ formatCurrency(sinistre.cout_societe) }}
            </div>
            <div class="col-md-6">
              <b>Coût assurance:</b>
              {{ formatCurrency(sinistre.cout_assurance) }}
            </div>
          </div>

          <div
            class="row mb-3"
            v-if="sinistre.garage_nom || sinistre.garage_adresse"
          >
            <div class="col-md-6">
              <b>Garage:</b> {{ sinistre.garage_nom || "Non défini" }}
            </div>
            <div class="col-md-6">
              <b>Adresse du garage:</b>
              {{ sinistre.garage_adresse || "Non définie" }}
            </div>
          </div>

          <div
            class="row mb-3"
            v-if="sinistre.etat !== null && sinistre.etat !== undefined"
          >
            <div class="col-md-6">
              <b>État du sinistre:</b>
              <span :class="getEtatTextClass(sinistre.etat)">
                {{ getEtatLabel(sinistre.etat) }}
              </span>
            </div>
          </div>

          <div class="row mb-3" v-if="sinistre.commentaires">
            <div class="col-md-12">
              <b>Commentaires:</b> {{ sinistre.commentaires }}
            </div>
          </div>

          <!-- Boutons d'action rapide selon l'état -->
          <div class="row mt-4" v-if="!isTraite(sinistre)">
            <div class="col-md-12 d-flex">
              <button
                v-if="sinistre.etat === 0"
                class="btn btn-sm btn-outline-primary mr-2"
                @click="actionExpertiser(sinistre)"
              >
                <i class="fas fa-clipboard-check mr-1"></i> Marquer en expertise
              </button>
              <button
                v-if="sinistre.etat === 1"
                class="btn btn-sm btn-outline-warning mr-2"
                @click="actionEnReparation(sinistre)"
              >
                <i class="fas fa-tools mr-1"></i> Marquer en réparation
              </button>
              <button
                v-if="sinistre.etat !== 3"
                class="btn btn-sm btn-outline-success"
                @click="actionTerminer(sinistre)"
              >
                <i class="fas fa-check mr-1"></i> Marquer comme traité
              </button>
            </div>
          </div>
        </div>

        <div class="pt-3" v-if="sinistre.id">
          <uploader
            :id="'doc_vehicule_sinistre_' + sinistre.id"
            url="/upload/document"
            file_system="VEHICULES_SINISTRES"
            file_field="sinistre"
            :file_key="sinistre.id"
            title="Documents du sinistre"
          ></uploader>
        </div>
      </div>
    </div>

    <div
      v-if="
        !nouveauSinistre &&
          (!vehicule.sinistres || vehicule.sinistres.length === 0)
      "
      class="alert alert-info"
    >
      Ce véhicule n'a pas encore de sinistre enregistré. Cliquez sur "Ajouter"
      pour déclarer un sinistre.
    </div>

    <!-- Tableau récapitulatif -->
    <div
      class="mt-4"
      v-if="vehicule.sinistres && vehicule.sinistres.length > 0"
    >
      <h5>Récapitulatif par état</h5>
      <table class="table table-bordered table-hover">
        <thead class="thead-light">
          <tr>
            <th>État</th>
            <th>Nombre</th>
            <th>Coût total société</th>
            <th>Coût total assurance</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="etat in etatsOptions" :key="etat.id">
            <td>
              <span :class="getEtatTextClass(etat.id)">
                {{ etat.description }}
              </span>
            </td>
            <td>{{ countSinistresByEtat(etat.id) }}</td>
            <td>{{ formatCurrency(totalCoutSocieteByEtat(etat.id)) }}</td>
            <td>{{ formatCurrency(totalCoutAssuranceByEtat(etat.id)) }}</td>
          </tr>
          <tr class="font-weight-bold bg-light">
            <td>Total</td>
            <td>
              {{ vehicule.sinistres.length }}
            </td>
            <td>{{ formatCurrency(totalCoutSociete) }}</td>
            <td>{{ formatCurrency(totalCoutAssurance) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex justify-content-between mt-4">
      <div class="card text-white bg-info mb-3" style="max-width: 18rem;">
        <div class="card-header">Statistiques</div>
        <div class="card-body">
          <h5 class="card-title">
            {{ vehicule.sinistres ? vehicule.sinistres.length : 0 }}
            sinistre(s)
          </h5>
          <p class="card-text">
            Coût total société: {{ formatCurrency(totalCoutSociete) }}<br />
            Coût total assurance: {{ formatCurrency(totalCoutAssurance) }}
          </p>
        </div>
      </div>

      <div
        v-if="hasSinistresEnCours()"
        class="card text-dark bg-warning mb-3"
        style="max-width: 24rem;"
      >
        <div class="card-header">
          <i class="fas fa-exclamation-circle mr-1"></i>
          Attention
        </div>
        <div class="card-body">
          <h5 class="card-title">
            Sinistres en cours
          </h5>
          <p class="card-text">
            Ce véhicule possède
            {{ getSinistresEnCoursCount() }} sinistre(s) en cours de traitement.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseFormRow from "@/components/bases/FormRow.vue";
import BaseInput from "@/components/bases/Input.vue";
import BaseSelect from "@/components/bases/Select.vue";
import BaseInputDatePicker from "@/components/bases/InputDatePicker.vue";
import BaseButton from "@/components/bases/Button.vue";
import ButtonCircle from "@/components/bases/ButtonCircle.vue";
import Uploader from "@/components/bases/Uploader.vue";
import SelectCollaborateur from "@/components/bases/SelectCollaborateur.vue";

export default {
  name: "VehiculeSinistres",
  components: {
    BaseToolBar,
    BaseFormRow,
    BaseInput,
    BaseSelect,
    BaseInputDatePicker,
    BaseButton,
    ButtonCircle,
    Uploader,
    SelectCollaborateur
  },
  props: {
    vehicule: {
      type: Object,
      required: true
    },
    statut: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      nouveauSinistre: null,
      sinistreEnCoursEdition: null,
      isLoading: false,
      feedback: {
        message: "",
        type: "info"
      },
      typesDommage: [
        { id: 0, description: "Carrosserie" },
        { id: 1, description: "Mécanique" },
        { id: 2, description: "Bris de glace" },
        { id: 3, description: "Vol" },
        { id: 4, description: "Incendie" },
        { id: 5, description: "Autre" }
      ],
      circonstancesDommage: [
        { id: 0, description: "Accident de la route" },
        { id: 1, description: "Accident sur parking" },
        { id: 2, description: "Acte de vandalisme" },
        { id: 3, description: "Catastrophe naturelle" },
        { id: 4, description: "Autre" }
      ],
      optionsResponsabilite: [
        { id: 0, description: "0% (non responsable)" },
        { id: 1, description: "50% (responsabilité partagée)" },
        { id: 2, description: "100% (responsable)" },
        { id: 3, description: "Non déterminée" }
      ],
      optionsFranchise: [
        { id: 0, description: "Pas de franchise" },
        { id: 1, description: "Franchise appliquée" },
        { id: 2, description: "Non déterminé" }
      ],
      etatsOptions: [
        { id: 0, description: "Déclaration en cours" },
        { id: 1, description: "En expertise" },
        { id: 2, description: "En réparation" },
        { id: 3, description: "Traité" }
      ]
    };
  },
  computed: {
    collaborateursOptions() {
      if (!this.vehicule.collaborateurs) return [];

      return this.vehicule.collaborateurs
        .filter(collab => collab && collab.collaborateur)
        .map(collab => {
          return {
            id: collab.collaborateur.id,
            description: `${collab.collaborateur.collaborateur_nom} ${collab
              .collaborateur.collaborateur_prenom || ""}`
          };
        })
        .sort((a, b) => a.description.localeCompare(b.description));
    },
    totalCoutSociete() {
      if (!this.vehicule.sinistres || this.vehicule.sinistres.length === 0)
        return 0;
      return this.vehicule.sinistres.reduce((total, sinistre) => {
        return total + (parseFloat(sinistre.cout_societe) || 0);
      }, 0);
    },
    totalCoutAssurance() {
      if (!this.vehicule.sinistres || this.vehicule.sinistres.length === 0)
        return 0;
      return this.vehicule.sinistres.reduce((total, sinistre) => {
        return total + (parseFloat(sinistre.cout_assurance) || 0);
      }, 0);
    },
    isNewVehicule() {
      return !this.vehicule.id;
    }
  },
  mounted() {
    // Initialiser la liste des sinistres si elle n'existe pas
    if (!this.vehicule.sinistres) {
      this.$set(this.vehicule, "sinistres", []);
    }
  },
  methods: {
    addSinistre() {
      // Vérifier que le véhicule est bien enregistré
      if (!this.vehicule.id) {
        this.showFeedback(
          "Veuillez d'abord enregistrer le véhicule",
          "warning"
        );
        return;
      }

      // Fermer toute édition en cours
      this.sinistreEnCoursEdition = null;
      this.nouveauSinistre = null;

      // Créer immédiatement un sinistre en base de données
      this.isLoading = true;

      this.$http
        .post("/vehicules-sinistre", {
          vehicule_id: this.vehicule.id,
          etat: 0 // État initial: Déclaration en cours
        })
        .then(response => {
          // Vérifier que sinistres est initialisé
          if (!this.vehicule.sinistres) {
            this.$set(this.vehicule, "sinistres", []);
          }

          // Ajouter le sinistre retourné par l'API
          this.vehicule.sinistres.unshift(response.data);

          // Passer en mode édition sur ce nouveau sinistre
          this.editSinistre(response.data);

          this.showFeedback(
            "Sinistre créé avec succès. Vous pouvez maintenant le compléter.",
            "success"
          );
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    cancelNouveauSinistre() {
      this.nouveauSinistre = null;
    },
    editSinistre(sinistre) {
      // Créer une copie pour ne pas modifier directement l'original
      this.sinistreEnCoursEdition = { ...sinistre };
    },
    cancelEdit() {
      this.sinistreEnCoursEdition = null;
    },

    saveSinistreEnCours() {
      if (!this.sinistreEnCoursEdition || !this.sinistreEnCoursEdition.id)
        return;

      this.isLoading = true;
      this.$http
        .put(
          "/vehicules-sinistre/" + this.sinistreEnCoursEdition.id,
          this.sinistreEnCoursEdition
        )
        .then(response => {
          // Mettre à jour le sinistre dans le tableau
          const index = this.vehicule.sinistres.findIndex(
            s => s.id === this.sinistreEnCoursEdition.id
          );
          if (index !== -1) {
            this.$set(this.vehicule.sinistres, index, response.data);
          }
          this.sinistreEnCoursEdition = null;
          this.showFeedback("Sinistre mis à jour avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deleteSinistre(sinistre) {
      if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce sinistre ?")) {
        return;
      }

      // Si c'est un nouveau sinistre non encore sauvegardé
      if (!sinistre.id) {
        const index = this.vehicule.sinistres.findIndex(s => s === sinistre);
        if (index !== -1) {
          this.vehicule.sinistres.splice(index, 1);
        }
        return;
      }

      this.isLoading = true;
      this.$http
        .delete(`/vehicules-sinistre/${sinistre.id}`)
        .then(() => {
          const index = this.vehicule.sinistres.findIndex(
            s => s.id === sinistre.id
          );
          if (index !== -1) {
            this.vehicule.sinistres.splice(index, 1);
          }
          this.showFeedback("Sinistre supprimé avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Actions rapides pour changer l'état du sinistre
    actionExpertiser(sinistre) {
      const today = this.$moment().format("YYYY-MM-DD");
      const updatedSinistre = {
        ...sinistre,
        etat: 1,
        date_expertise: today
      };
      this.updateSinistreStatus(updatedSinistre);
    },
    actionEnReparation(sinistre) {
      const updatedSinistre = {
        ...sinistre,
        etat: 2
      };
      this.updateSinistreStatus(updatedSinistre);
    },
    actionTerminer(sinistre) {
      const updatedSinistre = {
        ...sinistre,
        etat: 3
      };
      this.updateSinistreStatus(updatedSinistre);
    },
    updateSinistreStatus(sinistre) {
      this.isLoading = true;
      this.$http
        .put(`/vehicules-sinistre/${sinistre.id}`, sinistre)
        .then(response => {
          const index = this.vehicule.sinistres.findIndex(
            s => s.id === sinistre.id
          );
          if (index !== -1) {
            this.$set(this.vehicule.sinistres, index, response.data);
          }
          this.showFeedback("État mis à jour avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Méthodes utilitaires
    getTypeDommageLabel(typeId) {
      if (typeId === null || typeId === undefined) return "Non défini";
      const type = this.typesDommage.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },
    getCirconstanceLabel(circonstanceId) {
      if (circonstanceId === null || circonstanceId === undefined)
        return "Non définie";
      const circonstance = this.circonstancesDommage.find(
        c => c.id === parseInt(circonstanceId)
      );
      return circonstance ? circonstance.description : "Non définie";
    },
    getResponsabiliteLabel(responsabiliteId) {
      if (responsabiliteId === null || responsabiliteId === undefined)
        return "Non définie";
      const responsabilite = this.optionsResponsabilite.find(
        r => r.id === parseInt(responsabiliteId)
      );
      return responsabilite ? responsabilite.description : "Non définie";
    },
    getFranchiseLabel(franchiseId) {
      if (franchiseId === null || franchiseId === undefined)
        return "Non défini";
      const franchise = this.optionsFranchise.find(
        f => f.id === parseInt(franchiseId)
      );
      return franchise ? franchise.description : "Non définie";
    },
    getEtatLabel(etatId) {
      if (etatId === null || etatId === undefined) return "Non défini";
      const etat = this.etatsOptions.find(e => e.id === parseInt(etatId));
      return etat ? etat.description : "Non défini";
    },
    getCollaborateurName(collaborateurId) {
      if (!collaborateurId) return "Non désigné";

      const option = this.collaborateursOptions.find(
        c => c.id === collaborateurId
      );
      return option ? option.description : "Collaborateur inconnu";
    },
    countSinistresByEtat(etatId) {
      if (!this.vehicule.sinistres) return 0;
      return this.vehicule.sinistres.filter(
        s => s.etat !== null && parseInt(s.etat) === etatId
      ).length;
    },
    totalCoutSocieteByEtat(etatId) {
      if (!this.vehicule.sinistres) return 0;
      return this.vehicule.sinistres
        .filter(s => s.etat !== null && parseInt(s.etat) === etatId)
        .reduce((total, s) => total + (parseFloat(s.cout_societe) || 0), 0);
    },
    totalCoutAssuranceByEtat(etatId) {
      if (!this.vehicule.sinistres) return 0;
      return this.vehicule.sinistres
        .filter(s => s.etat !== null && parseInt(s.etat) === etatId)
        .reduce((total, s) => total + (parseFloat(s.cout_assurance) || 0), 0);
    },
    isEnAttente(sinistre) {
      return sinistre.etat !== null && parseInt(sinistre.etat) < 3;
    },
    isTraite(sinistre) {
      return sinistre.etat !== null && parseInt(sinistre.etat) === 3;
    },
    hasSinistresEnCours() {
      if (!this.vehicule.sinistres || this.vehicule.sinistres.length === 0)
        return false;
      return this.vehicule.sinistres.some(
        s => s.etat !== null && parseInt(s.etat) < 3
      );
    },
    getSinistresEnCoursCount() {
      if (!this.vehicule.sinistres) return 0;
      return this.vehicule.sinistres.filter(
        s => s.etat !== null && parseInt(s.etat) < 3
      ).length;
    },
    getEtatBadgeClass(etatId) {
      etatId = parseInt(etatId);
      switch (etatId) {
        case 0:
          return "badge-warning"; // Déclaration en cours
        case 1:
          return "badge-info"; // En expertise
        case 2:
          return "badge-primary"; // En réparation
        case 3:
          return "badge-success"; // Traité
        default:
          return "badge-secondary";
      }
    },
    getEtatTextClass(etatId) {
      etatId = parseInt(etatId);
      switch (etatId) {
        case 0:
          return "text-warning"; // Déclaration en cours
        case 1:
          return "text-info"; // En expertise
        case 2:
          return "text-primary"; // En réparation
        case 3:
          return "text-success"; // Traité
        default:
          return "text-secondary";
      }
    },
    formatDate(date) {
      if (!date) return "";
      return this.$moment(date).format("DD/MM/YYYY");
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR"
      }).format(value || 0);
    },
    showFeedback(message, type = "info") {
      this.feedback = { message, type };
      // Faire disparaître le message après quelques secondes
      setTimeout(() => {
        this.feedback = { message: "", type: "info" };
      }, 3000);
    }
  }
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}

.badge-primary {
  background-color: #007bff;
  color: #fff;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-danger {
  background-color: #dc3545;
  color: #fff;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

.badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-primary {
  color: #007bff !important;
}

.text-success {
  color: #28a745 !important;
}

.text-secondary {
  color: #6c757d !important;
}

.border-warning {
  border-color: #ffc107 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
}

.border-danger {
  border-color: #dc3545 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.thead-light th {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}
</style>
